import { PRODUCT_FEATURE_KEYS } from "@vetty/contracts";
import type { GroupKind, GroupLicenseStatus } from "../../_types/groups.types";

export const GROUP_PRODUCT_FEATURE_OPTIONS = [...PRODUCT_FEATURE_KEYS] as const;

export type GroupLicenseStatusOption = GroupLicenseStatus;

export type GroupFormTab =
  | "identity"
  | "features"
  | "license"
  | "accounts";

/** Explicit `model:*` keys from the contracts registry only. */
export const GROUP_MODEL_FEATURE_KEYS = GROUP_PRODUCT_FEATURE_OPTIONS.filter(
  (key): key is `model:${string}` => key.startsWith("model:"),
);

export type GroupFeatureMeta = {
  key: string;
  label: string;
  group: "models" | "product";
};

const MODEL_LABELS: Record<string, string> = {
  bee: "Bee",
  horse: "Horse",
  cow: "Cow",
  pig: "Pig",
  cat: "Cat",
  dog: "Dog",
  rat: "Rat",
  fish: "Fish",
  chicken: "Chicken",
};

const PRODUCT_FEATURE_META: Record<string, string> = {
  catalog: "Catalog",
  exam_mode: "Exam mode",
};

/** Readable labels for allowlisted product features (no permission keys). */
export const GROUP_FEATURE_META: GroupFeatureMeta[] =
  GROUP_PRODUCT_FEATURE_OPTIONS.map((key) => {
    if (key.startsWith("model:")) {
      const slug = key.slice("model:".length);
      return {
        key,
        label: MODEL_LABELS[slug] ?? slug,
        group: "models" as const,
      };
    }
    return {
      key,
      label: PRODUCT_FEATURE_META[key] ?? key,
      group: "product" as const,
    };
  });

export const GROUP_PRODUCT_FEATURE_META = GROUP_FEATURE_META.filter(
  (f) => f.group === "product",
);

export const LICENSE_STATUS_OPTIONS: Array<{
  value: GroupLicenseStatusOption;
  label: string;
}> = [
  { value: "active", label: "Active" },
  { value: "scheduled", label: "Scheduled" },
  { value: "revoked", label: "Revoked" },
];

export const licenseStatusLabel = (
  value: GroupLicenseStatusOption,
): string =>
  LICENSE_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value;

export const licenseStatusFromLabelOrValue = (
  next: string,
): GroupLicenseStatusOption | null => {
  const byValue = LICENSE_STATUS_OPTIONS.find((o) => o.value === next);
  if (byValue) return byValue.value;
  const byLabel = LICENSE_STATUS_OPTIONS.find((o) => o.label === next);
  return byLabel?.value ?? null;
};

/** Format epoch ms as UTC `YYYY-MM-DDTHH:mm` for the shared date+time contract. */
export const toUtcDateTimeLocal = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

/** Parse UTC datetime-local string to epoch ms; empty → null. */
export const fromUtcDateTimeLocal = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const iso = /Z$|[+-]\d{2}:\d{2}$/.test(trimmed) ? trimmed : `${trimmed}:00.000Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : NaN;
};

export const splitUtcDateTimeLocal = (
  value: string,
): { date: string; time: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { date: "", time: "" };
  const [date = "", timePart = ""] = trimmed.split("T");
  const time = timePart.slice(0, 5);
  return { date, time };
};

export const joinUtcDateTimeLocal = (date: string, time: string): string => {
  const d = date.trim();
  if (!d) return "";
  const t = (time.trim() || "00:00").slice(0, 5);
  return `${d}T${t}`;
};

/**
 * Convert a UTC civil date-only string (`YYYY-MM-DD`) to a local-noon Date for
 * react-day-picker. Local noon avoids DST “missing midnight” / previous-day
 * highlight bugs that appear with `Date.UTC(...)+getUTC*` in negative offsets.
 * Stored form values remain explicit UTC date digits; epoch conversion is unchanged.
 */
export const utcCivilDateStringToLocalNoonDate = (
  date: string,
): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!match) return undefined;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return undefined;
  }
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

/** Map a DayPicker local civil selection back to UTC date-only digits. */
export const localDateToUtcCivilDateString = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export type ModelsSelectionState = "none" | "partial" | "all";

export const getModelsSelectionState = (
  selected: readonly string[],
): ModelsSelectionState => {
  const selectedModels = GROUP_MODEL_FEATURE_KEYS.filter((k) =>
    selected.includes(k),
  );
  if (selectedModels.length === 0) return "none";
  if (selectedModels.length === GROUP_MODEL_FEATURE_KEYS.length) return "all";
  return "partial";
};

/** Parent "All models" checked / indeterminate derived from explicit model:* keys. */
export const getAllModelsCheckboxState = (
  selected: readonly string[],
): boolean | "indeterminate" => {
  const state = getModelsSelectionState(selected);
  if (state === "all") return true;
  if (state === "partial") return "indeterminate";
  return false;
};

export const setAllModelsSelected = (
  selected: readonly string[],
  selectAll: boolean,
): string[] => {
  const withoutModels = selected.filter((k) => !k.startsWith("model:"));
  if (!selectAll) return withoutModels;
  return [...withoutModels, ...GROUP_MODEL_FEATURE_KEYS];
};

/** Clear means an explicit empty feature array (not undefined). */
export const clearFeaturePolicy = (): string[] => [];

export type GroupAggregateBody = {
  name: string;
  description: string | null;
  kind: GroupKind;
  features: string[];
  license: {
    activeFrom: number;
    expiresAt: number | null;
    status: GroupLicenseStatusOption;
    seatLimit: number;
  } | null;
  accountIds: string[];
};

export type GroupAggregateValidationFailure = {
  ok: false;
  message: string;
  field?: string;
  tab: GroupFormTab;
};

export const buildGroupAggregateBody = (input: {
  name: string;
  description: string;
  kind: GroupKind;
  features: readonly string[];
  licenseEnabled: boolean;
  activeFromUtc: string;
  expiresAtUtc: string;
  status: GroupLicenseStatusOption;
  seatLimit: string;
  accountIds: readonly string[];
}):
  | { ok: true; body: GroupAggregateBody }
  | GroupAggregateValidationFailure => {
  const name = input.name.trim();
  if (!name) {
    return {
      ok: false,
      message: "Name is required",
      field: "name",
      tab: "identity",
    };
  }
  if (!["user", "device", "mixed"].includes(input.kind)) {
    return {
      ok: false,
      message: "Invalid group kind",
      field: "kind",
      tab: "identity",
    };
  }

  const accountIds = [
    ...new Set(input.accountIds.map((id) => id.trim()).filter(Boolean)),
  ];

  let license: GroupAggregateBody["license"] = null;
  if (input.licenseEnabled) {
    const activeFrom = fromUtcDateTimeLocal(input.activeFromUtc);
    if (activeFrom == null || Number.isNaN(activeFrom)) {
      return {
        ok: false,
        message: "activeFrom must be a valid UTC date/time",
        field: "activeFrom",
        tab: "license",
      };
    }

    let expiresAt: number | null = null;
    if (input.expiresAtUtc.trim()) {
      const parsed = fromUtcDateTimeLocal(input.expiresAtUtc);
      if (parsed == null || Number.isNaN(parsed)) {
        return {
          ok: false,
          message: "expiresAt must be a valid UTC date/time",
          field: "expiresAt",
          tab: "license",
        };
      }
      if (parsed <= activeFrom) {
        return {
          ok: false,
          message: "expiresAt must be after activeFrom",
          field: "expiresAt",
          tab: "license",
        };
      }
      expiresAt = parsed;
    }

    if (!["active", "scheduled", "revoked"].includes(input.status)) {
      return {
        ok: false,
        message: "Invalid license status",
        field: "status",
        tab: "license",
      };
    }

    const seatLimit = Number.parseInt(input.seatLimit, 10);
    if (!Number.isFinite(seatLimit) || seatLimit < 0) {
      return {
        ok: false,
        message: "seatLimit must be a non-negative integer",
        field: "seatLimit",
        tab: "license",
      };
    }
    if (seatLimit < accountIds.length) {
      return {
        ok: false,
        message: `seatLimit (${seatLimit}) is below selected membership count (${accountIds.length})`,
        field: "seatLimit",
        tab: "license",
      };
    }

    license = {
      activeFrom,
      expiresAt,
      status: input.status,
      seatLimit,
    };
  }

  return {
    ok: true,
    body: {
      name,
      description: input.description.trim() || null,
      kind: input.kind,
      features: [...input.features],
      license,
      accountIds,
    },
  };
};

export const formatApiError = (err: unknown, fallback: string): string => {
  const data = (
    err as {
      response?: { data?: { message?: string; code?: string }; status?: number };
    }
  )?.response;
  const message = data?.data?.message;
  const code = data?.data?.code;
  const status = data?.status;
  if ((status === 409 || status === 400 || status === 404) && message) {
    return code ? `${message} (${code})` : message;
  }
  return message || fallback;
};

export const fieldElementId = (field?: string): string | null => {
  switch (field) {
    case "name":
      return "group-form-name";
    case "kind":
      return "group-form-kind";
    case "activeFrom":
      return "group-form-active-from-date";
    case "expiresAt":
      return "group-form-expires-at-date";
    case "seatLimit":
      return "group-form-seat-limit";
    case "status":
      return "group-form-license-status";
    default:
      return null;
  }
};
